import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Avatar, Button, CssBaseline, TextField, InputAdornment, IconButton } from "@material-ui/core";
import { Box, Typography, Grid, Container, Tooltip } from "@material-ui/core";

// Services
import auth from "../../../../services/auth/auth";

// Components
import AlertComponent from "../../../elements/Alert";
import { DotFalling } from "../../../loader/Loaders";

// Providers
import { useLanguage } from "../../../../contexts/languageContext";

// Connectors
import backendConnector from "../../../../services/connectors/backendConnector";

// Icons
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

// Styles
import ResetPasswordFormStyle from "../../../../assets/styles/materialUI/questionnaire/authentication/forgotPassword/ResetPasswordFormStyle";

const ResetPasswordForm = ({ isLoading, setIsLoading, error, setError, message, setMessage }) => {
	const history = useHistory();
	const { token } = useParams();
	const { t, i18n } = useTranslation();
	const { direction } = useLanguage();
	const isArabicUI = document.documentElement?.dir === "rtl";
	const formRef = React.useRef();
	const classes = ResetPasswordFormStyle();
	const [count, setCount] = useState(0);
	const [password, setPassword] = useState(null);
	const [confirmedPassword, setConfirmedPassword] = useState(null);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmedPassword, setShowConfirmedPassword] = useState(false);
	const [isValid, setIsValid] = useState(null);
	const validatePassword = (pwd) => {
		const arabicLetterRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
		const arabicDigitRegex = /[\u0660-\u0669\u06F0-\u06F9]/;
		const rules = {
			length: pwd.length >= 8,
			letter: /[A-Za-z]/.test(pwd) || arabicLetterRegex.test(pwd),
			digit: /[0-9]/.test(pwd) || arabicDigitRegex.test(pwd),
			special:
				/[^A-Za-z0-9\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0660-\u0669\u06F0-\u06F9\s]/.test(
					pwd,
				),
		};

		return {
			valid: Object.values(rules).every(Boolean),
			rules,
		};
	};

	const resetPassword = async () => {
		if (!formRef.current.reportValidity()) return;
		else if (!auth.isActive()) {
			setError(t("common.licenseExpired"));
		} else {
			const { valid } = validatePassword(password);

			if (!valid) {
				setError(t("auth.signup.errors.weakPassword"));
				return;
			}

			if (password !== confirmedPassword) {
				setError(t("auth.signup.errors.passwordMismatch"));
				return;
			}
			setIsLoading(true);
			setError(null);
			let accountDetails = {
				password: password,
				token: token,
			};
			let response = await backendConnector.reset_password(accountDetails);
			if (response?.message) {
				setMessage(response.message);
				setCount(10);
			} else {
				setError(response?.error || "common.errors.passwordResetFailed");
			}
			setIsLoading(false);
		}
	};

	const PasswordTooltipContent = ({ password }) => {
		const { rules } = validatePassword(password || "");

		return (
			<div className={classes.passwordPolicy} dir={direction}>
				<div className={rules.length ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.length")}
				</div>
				<div className={rules.letter ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.letter")}
				</div>
				<div className={rules.digit ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.digit")}
				</div>
				<div className={rules.special ? classes.filledPasswordPolicy : classes.pendingPasswordPolicy}>
					{t("auth.signup.passwordRules.special")}
				</div>
			</div>
		);
	};

	const redirectLogin = () => {
		history.push("/questionnaire/login");
	};

	const redirectForgotPassword = () => {
		history.push("/questionnaire/forgot_password");
	};

	useEffect(() => {
		let tokenDetails = {
			token: token,
		};
		const validateToken = async () => {
			try {
				let response = await backendConnector.validateToken(tokenDetails);
				if (response?.message) {
					setIsValid(true);
				} else {
					setIsValid(false);
				}
			} catch (err) {
				console.error(err);
				setIsValid(false);
			}
		};

		validateToken();
	}, [token]);

	useEffect(() => {
		if (count <= 0) return;

		const timer = setInterval(() => {
			setCount((prevCount) => {
				if (prevCount <= 1) {
					clearInterval(timer);
					history.push("/questionnaire/login");
					return 0;
				}
				return prevCount - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [count, history]);

	const translatedMessage = message ? (i18n.exists(message) ? t(message) : message) : null;

	if (isLoading || isValid === null) {
		return (
			<Container component="main" maxWidth="xs" dir={direction}>
				<CssBaseline />
				<Grid className={classes.paper}>
					<Grid className={`${classes.loaderContainer} h-100 w-100 d-flex justify-content-center`}>
						<Grid className={classes.loader}>
							<DotFalling />
						</Grid>
					</Grid>
				</Grid>
			</Container>
		);
	}

	return (
		<>
			{isValid && (
				<Container component="main" maxWidth="xs" dir={direction}>
					<CssBaseline />
					<Grid className={classes.paper}>
						<Avatar className={classes.avatar}>
							<VpnKeyIcon />
						</Avatar>
						<Typography variant="h6" color="textSecondary" align="center">
							{t("auth.reset.title")}
						</Typography>
						<form className={classes.form} ref={formRef}>
							{/* {error && <Alert severity="error">{error}</Alert>} */}
							<Tooltip
								title={<PasswordTooltipContent password={password} />}
								placement={direction === "rtl" ? "left-start" : "right-start"}
								arrow
							>
								<TextField
									variant="filled"
									margin="normal"
									required
									fullWidth
									disabled={message}
									id="password"
									label={t("auth.reset.password")}
									name="password"
									type="text"
									autoComplete="new-password"
									value={password}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label={
														showPassword
															? t("common.buttons.hidePassword")
															: t("common.buttons.showPassword")
													}
													onClick={() => setShowPassword((prev) => !prev)}
													onMouseDown={(e) => e.preventDefault()}
													edge="end"
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
									inputProps={{
										lang: isArabicUI ? "ar" : "en",
										dir: "auto",
										inputMode: "text",
										autoCapitalize: "off",
										autoCorrect: "off",
										spellCheck: "false",
										style: {
											WebkitTextSecurity: showPassword ? "none" : "disc",
										},
									}}
									onChange={(e) => setPassword(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") resetPassword();
									}}
								/>
							</Tooltip>
							<TextField
								variant="filled"
								margin="normal"
								required
								fullWidth
								disabled={message}
								id="confirmedPassword"
								label={t("auth.reset.confirmPassword")}
								name="confirmedPassword"
								type="text"
								autoComplete="new-password"
								value={confirmedPassword}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label={
													showConfirmedPassword
														? t("common.buttons.hidePassword")
														: t("common.buttons.showPassword")
												}
												onClick={() => setShowConfirmedPassword((prev) => !prev)}
												onMouseDown={(e) => e.preventDefault()}
												edge="end"
											>
												{showConfirmedPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
								inputProps={{
									lang: isArabicUI ? "ar" : "en",
									dir: "auto",
									inputMode: "text",
									autoCapitalize: "off",
									autoCorrect: "off",
									spellCheck: "false",
									style: {
										WebkitTextSecurity: showConfirmedPassword ? "none" : "disc",
									},
								}}
								onChange={(e) => setConfirmedPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") resetPassword();
								}}
							/>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								className={classes.submit}
								onClick={resetPassword}
							>
								{t("auth.reset.submit")}
							</Button>
						</form>
					</Grid>
					<Box mt={1} className={classes.normalSeperator}>
						<Typography variant="body2" color="textSecondary" align="center">
							{t("auth.login.or")}
						</Typography>
					</Box>
					<Box mt={1} className={classes.smallSeperator}>
						<Typography variant="body2" color="textSecondary" align="center">
							{t("auth.login.or")}
						</Typography>
					</Box>
					<Button
						fullWidth
						variant="contained"
						color="secondary"
						className={classes.submit}
						onClick={redirectLogin}
					>
						{t("auth.forgot.back")}
					</Button>
					<Grid className={`${classes.loaderContainer} h-100 w-100 d-flex justify-content-center`}>
						<Grid className={classes.loader}>{isLoading && <DotFalling />}</Grid>
					</Grid>
				</Container>
			)}
			{isValid === false && (
				<Container component="main" maxWidth="xs" dir={direction}>
					<Grid className={classes.paper}>
						<CssBaseline />
						<Avatar className={`${classes.avatar} ${classes.headerAvatar}`}>
							<VpnKeyIcon />
						</Avatar>
						<Typography variant="h6" align="center" color="primary">
							{t("auth.reset.invalidLinkTitle")}
						</Typography>
						<Typography variant="body1" color="textSecondary" className={classes.invalidLinkBody}>
							{t("auth.reset.invalidLinkBody")}
						</Typography>
						<br />
						<Typography variant="body2" color="textSecondary" className={classes.invalidLinkBody2}>
							{t("auth.reset.invalidLinkBody2")}
						</Typography>
						<br />
						<Button
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							onClick={redirectForgotPassword}
						>
							{t("auth.reset.requestNew")}
						</Button>
						<Box mt={1} className={classes.normalSeperator}>
							<Typography variant="body2" color="textSecondary" align="center">
								{t("auth.login.or")}
							</Typography>
						</Box>
						<Box mt={1} className={classes.smallSeperator}>
							<Typography variant="body2" color="textSecondary" align="center">
								{t("auth.login.or")}
							</Typography>
						</Box>
						<Button
							fullWidth
							variant="contained"
							color="secondary"
							className={classes.submit}
							onClick={redirectLogin}
						>
							{t("auth.forgot.back")}
						</Button>
						<Grid className={`${classes.loaderContainer} h-100 w-100 d-flex justify-content-center`}>
							<Grid className={classes.loader}>{isLoading && <DotFalling />}</Grid>
						</Grid>
					</Grid>
				</Container>
			)}
			{(error || message) && (
				<AlertComponent
					errors={error ? [error] : []}
					messages={
						message
							? [t("auth.signup.redirectCountdown", { message: translatedMessage, seconds: count })]
							: []
					}
					setError={setError}
					setMessage={setMessage}
				/>
			)}
		</>
	);
};

export default ResetPasswordForm;
